import React, { useContext, useMemo, useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { Collapse, TextTitle, ValidatedBootstrapPanel, useCollapseState, ValidatedFixedPanel, ValidatedPanel, collapseExpander } from '@bingads-webui-campaign/wizard-jumbo-card';
import { ValidatedLegacyAdSchedule } from '@bingads-webui-campaign/ad-schedule';
import { Campaign } from '@bingads-webui/campaign-odata-schemas';
import { ValidationGroup } from '@bingads-webui-react/validation';
import { Panel as BootstrapPanel } from 'react-bootstrap';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  FormErrorGroup,
  entityIs,
  anyComp,
} from '@bingads-webui-campaign-react/form-error';
import {
  withControlErrorHandling,
  withUnvalidatedControlErrors,
} from '@bingads-webui-campaign-react/error-handling';
import { SEARCH_TYPE } from '@bingads-webui-react/bing-maps';
import { isXandrServing, ScopeLevel } from '@bingads-webui-campaign-react/constants';
import { Stack } from '@fluentui/react';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { useWizardService, matchStepState, STEP_STATES, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { getLocationTargetTitleDetails, getAdScheduleDetails } from '@bingads-webui-campaign-react/wizard-shared-components';
import { ValidatedConversionGoals, ValidatedLocationTargetSetting, useIsMounted } from '@bingads-webui-campaign-react/step-wizard-controls';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { getDateSettingDetails, getLaterDate } from '@bingads-webui-campaign/date-setting';
import { RemainingCharsValidatedTextField } from '@bingads-webui-campaign-react/remaining-chars-hint';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { useFormService } from '@bingads-webui-react/form';
import { campaignNameProp, DealImpressionPanel, Device, getDeviceDetails } from '@bingads-webui-campaign-react/xandr-campaign';
import { CampaignSubTypes } from '@bingads-webui-campaign/audience-campaign-shared';
import { getUrlOptionsDetails } from '@bingads-webui-campaign-react/url-options';
import { ValidatedUrlOptionsParameter, UrlOptionsImg } from '@bingads-webui-campaign-react/validated-url-input';
import { DisclaimerStringMap } from '@bingads-webui-campaign-react/ads-campaign-disclaimers';
import { DisclaimerAdExtension } from '@bingads-webui-campaign/campaign-ui-schemas';
import Repository from '@bingads-webui-campaign-legacy/adextension-shared/service/repository';
import { ServerValidatedWebExclusions, webExclusionPropertyVal } from '@bingads-webui-react/web-exclusions';
import { ThirdPartyMeasurementV3WithValidation, formatVendorSettings } from '@bingads-webui-campaign-react/third-party-measurement';
import { getMessageType } from '@bingads-webui-campaign-react/campaign-settings-utils';

import { TargetingEstimation } from '../adgroup/components/targeting-estimation';
import { StepHeader } from '../../shared/step-header';
import { XandrBudgetAndBidStrategy } from './components/xandr-budget-bid-strategy';
import { AudienceBudgetBidStrategy } from './components/audience-budget-bid-strategy';
import { BudgetBidStrategyDetails } from './components/budget-bid-strategy-details';
import { CampaignGoal } from './components/campaign-goal';
import { XandrBidValue, AudienceBidValue } from '../../shared/components';
import { SectionShimmer } from '../../shared/section-shimmer';
import { ValidatedBidAdjustment } from './components/audience-bid-adjustment';
import { ValidatedBidAdjustment as ValidatedBidAdjustmentGrid } from '../adgroup/components/audience-bid-adjustment';
import { CallToActionOptOut } from './components/call-to-action-opt-out';
import {
  isAudienceCampaign as isAudienceCampaignCreate,
  isAudienceCampaignEdit as isAudienceCampaignEditFlow,
  isEdit as isEditFlow,
  isDisplayAdsCampaign,
  isVideoAdsCampaign,
  isCTVOrDeal,
  isCTVCampaign,
} from '../../step-constants';
import { getCampaignStyles } from './styles';
import { getCampaignPanels } from '../../step-utils';
import adScheduleIcon from '../../media/ad-schedule.svg';
import bidAdjustmentIcon from '../../media/bid-adjustment.svg';
import bidValueIcon from '../../media/bid-value.svg';
import budgetBidStrategyIcon from '../../media/budget-bid-strategy.svg';
import conversionGoalsIcon from '../../media/conversion-goals.svg';
import startDateEndDateIcon from '../../media/start-date-end-date.svg';
import locationTargetIcon from '../../media/location-target.svg';
import phoneDesktopIcon from '../../media/phone-desktop-regular.svg';
import webExclusionsIcon from '../../media/web-exclusions.svg';
import CallToActionImg from '../../media/call-to-action.svg';
import Ruler from '../../shared/media/ruler.svg';
import { BiddingSchemeToBidType, BudgetTypes } from '../../common/constants';
import { ERROR_ENTITY } from '../../common/error-entities';
import { ValidatedDisclaimerExtension, getAssociatedDisclaimer } from './components/disclaimer-extension';
import { ValidatedAudienceDateSetting, getAudienceDateSettingsDetails } from './components/audience-date-setting';
import { WebsiteLists } from './components/website-lists';

const getPanelErrorProps = ({ needsAttention }) => (needsAttention ? {
  error: _TL_("* Something's wrong"),
} : {});


const adGroupLocationCriterions = anyComp(
  entityIs(ERROR_ENTITY.CampaignLocation),
  entityIs(ERROR_ENTITY.CampaignRadius),
  entityIs(ERROR_ENTITY.CampaignNegativeLocation),
  entityIs(ERROR_ENTITY.CampaignLocationIntent)
);

const ServerValidatedLocationTargetSetting = withControlErrorHandling(withUnvalidatedControlErrors(
  ValidatedLocationTargetSetting,
  { filter: adGroupLocationCriterions }
));

export const CampaignStepShell = ({
  data,
  onDataChange: onDataChangeProp,
  error,
}) => {
  const i18n = useContext(I18nContext);

  const isMounted = useIsMounted();

  const classes = useDefaultStyles(getCampaignStyles);
  const serverContext = useContext(ServerContext);
  const { DynamicPermissions = {} } = serverContext || {};
  const {
    ConversionAssociationWarning: IsConversionAssociationWarningEnabled,
    CampaignLifetimeBudget,
    MaeLifetimeBudgetExclusion,
    AudienceUsePrivacyCheckV2,
  } = DynamicPermissions;

  const isCampaignLifetimeBudgetEnabled = CampaignLifetimeBudget && !MaeLifetimeBudgetExclusion;

  const { state, send } = useWizardService();
  const wizardData = state.context.wizardData || {};
  const {
    flow, isDefaultCampaignStepModel, adGroup: initialAdGroup, campaign: initialCampaign, lifetimeBudgetFlowData = {},
  } = wizardData;
  const { isBudgetTypeEditable = false, isDateManagementEditable = false } = lifetimeBudgetFlowData;

  const currentDate = useMemo(() => new Date(), []);

  const activity = useContext(ActivityContext);

  const currentStates = state.toStrings()[1];

  const { basicOptions, advancedOptions } = getCampaignPanels(flow, serverContext);

  const isAudienceCampaignEdit = useMemo(() => isAudienceCampaignEditFlow(flow), [flow]);
  const isAudienceCampaign = useMemo(() => isAudienceCampaignCreate(flow) || isAudienceCampaignEdit, [flow, isAudienceCampaignEdit]);
  const isEdit = useMemo(() => isEditFlow(flow), [flow]);
  const isCTVOrDealFlow = useMemo(() => isCTVOrDeal(flow), [flow]);
  const isCTVFlow = useMemo(() => isCTVCampaign(flow), [flow]);

  const isLifetimeBudgetFeatureEnabled = isCampaignLifetimeBudgetEnabled && isAudienceCampaign;

  const allowStartDateEdit = useMemo(() => {
    if (initialCampaign && initialCampaign.UseCampaignLevelDates) {
      return initialCampaign.StartDate > currentDate;
    } else if (initialAdGroup) {
      return initialAdGroup.StartDate > currentDate;
    }

    return true;
  }, [currentDate, initialAdGroup, initialCampaign]);

  const [adScheduleView, setAdScheduleView] = useState(null);
  const setAdScheduleViewCallback = useCallback((view) => {
    setAdScheduleView(view);
  }, []);

  const onDataChange = useCallback((...args) => {
    if (!isMounted.current) return;
    onDataChangeProp(...args);
  }, [isMounted, onDataChangeProp]);

  const webExclusionsError = useMemo(() => error && error.filter(err => err.property === 'webExclusions'), [error]);
  const setExclusionsCallback = useCallback((exclusionsData, type) => {
    if (data) {
      const patchedData = {
        data: {
          ...data,
          exclusions: {
            ...(data.exclusions || {}),
            campaignExclusions: {
              ...(data.exclusions && data.exclusions.campaignExclusions ? data.exclusions.campaignExclusions : {}),
              [type]: exclusionsData,
            },
          },
        },
      };
      onDataChange(patchedData);
    }
  }, [data, onDataChange]);
  const onExclusionsListSelected = useCallback((exclusionsList) => {
    if (data) {
      const patchedData = {
        data: {
          ...data,
          websiteExclusionLists: exclusionsList.map(list => ({ ...list, Id: list.value })),
        },
      };
      onDataChange(patchedData);
    }
  }, [data, onDataChange]);

  const { scope } = useContext(ScopeContext) || {};

  const { aid, cid } = scope.values;
  const advertiserCustomerId = serverContext.CurrentAccount.AdvertiserCustomerID;

  const [optionsShown, setOptionsShown] = useState(basicOptions);

  const {
    adGroup, campaign, bundleCountries, bundleLocationCriterions, dealDetails,
  } = data || {};

  const { Id: adGroupId } = adGroup || {};

  const dateSettings = useMemo(() => ({
    StartDate: campaign && campaign.UseCampaignLevelDates ? campaign && campaign.StartDate : adGroup && adGroup.StartDate,
    EndDate: campaign && campaign.UseCampaignLevelDates ? campaign && campaign.EndDate : adGroup && adGroup.EndDate,
    UseCampaignLevelDates: campaign && campaign.UseCampaignLevelDates,
  }), [adGroup, campaign]);

  const dateSettingsDetails = useMemo(
    () => (isAudienceCampaign && dateSettings
      ? getAudienceDateSettingsDetails({
        i18n,
        isEdit,
        dateSettings,
        isLifetimeBudgetFeatureEnabled: isCampaignLifetimeBudgetEnabled,
      })
      : getDateSettingDetails(
        i18n,
        dateSettings.StartDate,
        dateSettings.EndDate,
        dateSettings.UseCampaignLevelDates,
        isLifetimeBudgetFeatureEnabled
      )),
    [isCampaignLifetimeBudgetEnabled, dateSettings, i18n, isAudienceCampaign, isEdit, isLifetimeBudgetFeatureEnabled]
  );

  const isLifetimeBudget = (campaign && campaign.Budget && campaign.Budget.Type === BudgetTypes.LifetimeBudgetStandard) || false;

  const disclaimerRepositoryRef = useRef(new Repository({
    schema: DisclaimerAdExtension,
    skipExtensionCache: true,
  }));

  const { Id: campaignId, adSchedules, locationTargets } = campaign || {};

  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap, DisclaimerStringMap]), [i18n]);

  const { state: { context: { saveFailed: showAllValidationErrors } } } = useFormService();

  const urlOptions = useMemo(() => ({
    TrackingUrlTemplate: (campaign && campaign.TrackingUrlTemplate) || null,
    FinalUrlSuffix: (campaign && campaign.FinalUrlSuffix) || null,
    UrlCustomParameters: (campaign && campaign.UrlCustomParameters) || null,
  }), [campaign]);
  const urlOptionsDetails = useMemo(() => getUrlOptionsDetails(i18n, urlOptions), [urlOptions, i18n]);

  const devicesDetails = useMemo(() => ((!campaign || !campaign.campaignLevelDeviceTargets)
    ? ''
    : getDeviceDetails(campaign.campaignLevelDeviceTargets, false, i18n)), [campaign, i18n]);

  const onValidate = (result) => {
    if (data && data.campaign) {
      let adGroupData = {};
      let campaignData = {};
      if (result && result.data) {
        if (result.data.BiddingScheme && result.data.BiddingScheme.Type === 'MaxConversions') {
          // for max conv we hide the bid panel
          const filterOutBidPanel = optionsShown.filter(option => option !== 'bid-value');
          adGroupData = {
            CpmBid: null,
            CpvBid: null,
          };
          setOptionsShown([...filterOutBidPanel]);
        } else {
          setOptionsShown([...basicOptions]);
        }

        if (result.data.StartDate) {
          adGroupData = {
            ...adGroupData,
            StartDate: result.data.StartDate,
            EndDate: result.data.EndDate,
          };
        }

        if (result.data.Budget && result.data.Budget.Type === BudgetTypes.LifetimeBudgetStandard) {
          const { StartDate, EndDate } = dateSettings;
          if (StartDate) {
            campaignData = {
              StartDate,
              EndDate: EndDate || getLaterDate(StartDate, 365),
              UseCampaignLevelDates: true,
            };
          }
        }
      }

      const patchedData = {
        data: {
          ...data,
          campaign: {
            ...data.campaign,
            ...result.data,
            ...campaignData,
          },
        },
        isValid: result.isValid,
      };

      if (!isEdit) {
        let pricingModelUpdate = {};
        if (result.data) {
          pricingModelUpdate = {
            PricingModel: result.data.BiddingScheme && result.data.BiddingScheme.Type,
          };
        }
        patchedData.data.adGroup = {
          ...data.adGroup,
          ...pricingModelUpdate,
          ...adGroupData,
        };
      }

      if (isEdit && IsConversionAssociationWarningEnabled) {
        const message = getMessageType(data, patchedData.data);
        if (message) {
          const updatedData = {
            wizardData: {
              ...wizardData,
              customConfirmMessages: [message],
            },
          };

          send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, updatedData.wizardData);
        }
      }

      onDataChange(patchedData);
    }
  };

  const onAdGroupValidate = (result) => {
    if (adGroup) {
      onDataChange({
        data: {
          ...data,
          adGroup: {
            ...data.adGroup,
            ...result.data,
          },
        },
        isValid: result.isValid,
      });
    }
  };

  const [
    allCampaignSettingsSections,
    campaignSettingsExpandedSections,
    setCampaignSettingsExpandedSections,
  ] = useCollapseState(
    optionsShown /* all sections */,
    ['campaign-name', 'bid-and-budget', 'bid-value', 'conversions', 'start-end-dates', 'disclaimer'] /* initially expanded sections */
  );

  const [
    allAdvancedCampaignSettingsSections,
    advancedCampaignSettingsExpandedSections,
    setAdvancedCampaignSettingsExpandedSections,
  ] = useCollapseState(
    advancedOptions /* all sections */,
    [''] /* initially expanded sections */
  );

  const advancedSettingsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setAdvancedCampaignSettingsExpandedSections }),
    [setAdvancedCampaignSettingsExpandedSections]
  );

  const {
    DynamicPermissions: {
      XandrNegativeTarget,
      EnableMSANDisplayCampaigns,
      EnableMSANVideoCampaigns,
      EnableDoubleVerify,
      PTPerfEstimationUIUpdate,
      CampaignCreationPlannerIntegration,
      WebsiteExclusionLists,
    },
    Permissions: {
      IsMSANDisclaimerEnabled,
      IsMSANISPOTEnabled,
    },
  } = serverContext;

  const isValidTargets = useMemo(() => {
    if (data && data.adGroup && campaign && campaign.BiddingScheme &&
      (data.adGroup.UseOptimizedTargeting === false || CampaignCreationPlannerIntegration) &&
      (data.adGroup[BiddingSchemeToBidType[campaign.BiddingScheme.Type]] > 0) &&
      campaign.Budget.Amount > 0
    ) {
      return false; // false until planner supports all bid strategies
    }
    return false;
  }, [CampaignCreationPlannerIntegration, campaign, data]);

  const BudgetAndBidStrategy = isAudienceCampaign ||
    (EnableMSANDisplayCampaigns && isDisplayAdsCampaign(flow)) ||
    (EnableMSANVideoCampaigns && isVideoAdsCampaign(flow)) ?
    AudienceBudgetBidStrategy : XandrBudgetAndBidStrategy;

  const BidValue = isAudienceCampaign ||
    (EnableMSANDisplayCampaigns && isDisplayAdsCampaign(flow)) ||
    (EnableMSANVideoCampaigns && isVideoAdsCampaign(flow)) ?
    AudienceBidValue : XandrBidValue;

  let campaignSubType = null;
  if (isAudienceCampaign) {
    campaignSubType = CampaignSubTypes.AudienceStatic;
  } else if (EnableMSANDisplayCampaigns && isDisplayAdsCampaign(flow)) {
    campaignSubType = CampaignSubTypes.DisplayAds;
  } else if (EnableMSANVideoCampaigns && isVideoAdsCampaign(flow)) {
    campaignSubType = CampaignSubTypes.BrandAwarenessVideoAds;
  }

  const DisclaimerAdsEnabled = useMemo(
    () =>
      _.result(_.find( // from campaign setting DB
        _.get(campaign, 'CampaignSettings', []),
        setting => setting['@odata.type'] === '#Model.DisclaimerSetting'
      ), 'DisclaimerAdsEnabled', false)
    , [campaign]
  );

  const verifiedTrackingSettingsValidationProps = useMemo(
    () => {
      const { campaign: campaignFromData } = data || {};
      if (_.isNull(campaignFromData) || _.isUndefined(campaignFromData)) {
        return { data: null };
      }
      const VerifiedTrackingSettings = _.get(campaignFromData, ['VerifiedTrackingSettings'], []);

      return {
        data: {
          VerifiedTrackingSettings,
        },
      };
    }
    , [data]
  );

  const shouldSelectAllDevices = useMemo(() => {
    if (campaign != null) {
      if (campaign.CampaignType === 'Audience'
        && campaign.initialCampaignLevelDeviceTargets
        && campaign.initialCampaignLevelDeviceTargets.length === 0) {
        return true;
      }
    }

    return false;
  }, [campaign]);

  const [associatedDisclaimer, setAssociatedDisclaimer] = useState(null);

  useEffect(() => {
    if (IsMSANDisclaimerEnabled && campaignId) {
      getAssociatedDisclaimer(campaignId, disclaimerRepositoryRef.current).then((disclaimer) => {
        if (!_.isEmpty(disclaimer)) {
          setAssociatedDisclaimer(disclaimer);
        }
      });
    }
    return null;
  }, [IsMSANDisclaimerEnabled, campaignId]);


  useEffect(() => {
    if (activity && matchStepState(state, STEP_STATES.LOADING)) {
      activity.signal(`onCampaignStepLoading - ${currentStates}`);
    }

    if (activity && matchStepState(state, STEP_STATES.SAVING)) {
      activity.signal(`onCampaignStepSaving - ${currentStates}`);
    }
  }, [activity, currentStates, state]);

  return (
    <div className={classNames(classes.campaignShell, 'campaign-shell', 'campaign-step')}>
      <ActuationGroup actuationProps={{ isActive: false }}>
        <AttentionBeaconGroup>
          <FormErrorGroup formErrorProps={{ errors: error || [], showAllValidationErrors }}>
            <ValidationGroup
              validationProps={{
                schema: Campaign,
                onValidate,
                data: (data && data.campaign) || {},
                i18n: legacyI18n,
              }}
            >
              <div className={classNames('campaign-step')}>
                {!isEdit &&
                  <div>
                    <Stack token={{ childrenGap: 14 }}>
                      <StepHeader
                        title={_TL_('Budget, bid, & campaign')}
                        description={_TL_('Tell us about your campaign and how much you want to spend.')}
                      />
                    </Stack>
                  </div>
                }
                <Stack horizontal token={{ childrenGap: 14 }}>
                  <Collapse allPanelItems={allCampaignSettingsSections} checkItems skipItemsTypeCheck activeKeys={campaignSettingsExpandedSections} setActiveKeys={setCampaignSettingsExpandedSections}>
                    <ValidatedFixedPanel
                      key="campaign-name"
                      title={<TextTitle titleKey={_TL_('Campaign name')} topicKey="pop_BA_CampaignCreate_Settings_CampaignName" />}
                      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                    >
                      <RemainingCharsValidatedTextField
                        placeholder={_TL_('Enter a name for your campaign')}
                        id="input-campaign-name"
                        validationProps={{
                          field: 'Name',
                          schema: Campaign,
                        }}
                        i18n={i18n}
                        formErrorProps={{ filter: campaignNameProp }}
                      />
                    </ValidatedFixedPanel>
                    <ValidatedFixedPanel
                      key="campaign-goal"
                      title={<TextTitle titleKey={_TL_('Campaign goal')} />}
                    >
                      {data && data.campaign ? (
                        <CampaignGoal campaign={data.campaign} />
                      ) : (<SectionShimmer count={1} />)}
                    </ValidatedFixedPanel>
                    <ValidatedPanel
                      key="bid-and-budget"
                      title={<TextTitle
                        titleKey={_TL_('Budget and Bid Strategy')}
                        icon={budgetBidStrategyIcon}
                        details={(campaign && isAudienceCampaign)
                          && <BudgetBidStrategyDetails budget={campaign.Budget} biddingScheme={campaign.BiddingScheme} />
                        }
                      />}
                      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                    >
                      {
                          data && data.campaign ? (
                            <BudgetAndBidStrategy
                              data={data}
                              onDataChange={onDataChange}
                              legacyI18n={legacyI18n}
                              campaignType="audience"
                              campaignSubType={campaignSubType}
                              isPremiumStreaming={isCTVFlow}
                              showBudgetTypeSelection={isLifetimeBudgetFeatureEnabled}
                              allowBudgetTypeSelection={!isEdit && isBudgetTypeEditable}
                              allowSharedBudgetLibrarySelection={!isLifetimeBudget || isBudgetTypeEditable}
                            />
                          ) : (<SectionShimmer count={3} />)
                        }
                    </ValidatedPanel>
                    <ValidatedPanel
                      key="start-end-dates"
                      title={<TextTitle
                        titleKey={_TL_('Start and end dates')}
                        details={
                          dateSettings
                            ? dateSettingsDetails : <SectionShimmer count={1} />
                        }
                        icon={startDateEndDateIcon}
                      />}
                      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                    >
                      {
                          campaign && campaign.Budget && dateSettings ? (
                            <ValidatedAudienceDateSetting
                              i18n={i18n}
                              validationProps={{
                                onValidate,
                                stopPropagation: true,
                                data: {
                                  StartDate: dateSettings.StartDate,
                                  EndDate: dateSettings.EndDate,
                                  UseCampaignLevelDates: dateSettings.UseCampaignLevelDates,
                                  BudgetType: campaign.Budget.Type,
                                  TimeZone: campaign.timeZone,
                                  isCreate: allowStartDateEdit || isDefaultCampaignStepModel,
                                },
                              }}
                              isEditFlow={isEdit}
                              displayDateManagementSelector={!isEdit && !isLifetimeBudget && isLifetimeBudgetFeatureEnabled}
                              allowDateManagementSelection={isDateManagementEditable}
                              allowNoEndDateSelection={!isLifetimeBudget}
                            />
                          ) : (<SectionShimmer count={3} />)
                        }
                    </ValidatedPanel>
                    <ValidatedPanel
                      key="conversions"
                      title={<TextTitle
                        titleKey={_TL_('Conversions')}
                        topicKey="pop_BA_PMAX_Conversions"
                        icon={conversionGoalsIcon}
                      />}
                      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                    >
                      {
                        data && data.campaign ? (
                          <ValidatedConversionGoals
                            campaign={data.campaign}
                            validationProps={{
                              initialValue: data.campaign,
                              data: data.campaign,
                            }}
                            cid={advertiserCustomerId}
                            aid={aid}
                            showTrackingStatusIcon={IsConversionAssociationWarningEnabled}
                          />
                        ) : (<SectionShimmer count={3} />)
                      }
                    </ValidatedPanel>
                    <ValidatedPanel
                      key="bid-value"
                      title={<TextTitle titleKey={_TL_('Default bid')} icon={bidValueIcon} topicKey="pop_BA_CampaignCreate_Budget_DefaultBid" />}
                      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                    >
                      {
                        data && data.adGroup ? (
                          <BidValue
                            data={data}
                            onDataChange={onDataChange}
                            legacyI18n={legacyI18n}
                          />
                        ) : (<SectionShimmer count={3} />)
                      }
                    </ValidatedPanel>
                    <ValidatedPanel
                      key="location-target"
                      data={data}
                      onDataChange={onDataChange}
                      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                      title={<TextTitle
                        titleKey={_TL_('Location')}
                        key="location-target"
                        topicKey="pop_BA_CampaignCreate_Settings_LocationTargeting"
                        panelKey="location-target"
                        details={getLocationTargetTitleDetails(i18n, locationTargets, false, '', true)}
                        icon={locationTargetIcon}
                      />}
                    >
                      {locationTargets ? (
                        <ServerValidatedLocationTargetSetting
                          validationProps={{
                            data: locationTargets,
                          }}
                          bundleCountries={bundleCountries}
                          bundleLocationCriterions={bundleLocationCriterions}
                          isRadiusExcludeEnabled={!isAudienceCampaign || !(EnableMSANDisplayCampaigns && EnableMSANVideoCampaigns)}
                          allowNegativeLocationTargeting={isAudienceCampaign ? true : XandrNegativeTarget}
                          showSearchTypeOptions={isAudienceCampaign || ((EnableMSANDisplayCampaigns && EnableMSANVideoCampaigns))}
                          defaultSelectedSearchType={SEARCH_TYPE.Boundary}
                          isBrandAwarenessCampaignScenario={isXandrServing(campaign.CampaignSubType, campaign.CampaignSettings)}
                          hideIntent
                          scope={scope}
                          bidStrategyType="placeholder"
                          strings={{
                            description: i18n.getString(_TL_('For example, a country/region, state/province, or ZIP/postal code.')),
                          }}
                        />
                      ) : (<SectionShimmer />)}
                    </ValidatedPanel>
                    <ValidatedPanel
                      key="disclaimer"
                      title={<TextTitle
                        titleKey={_TL_('Disclaimer')}
                        icon={bidAdjustmentIcon}
                        topicKey="pop_BA_Audience_Disclaimers"
                      />}
                      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                    >
                      {data && data.campaign ? (
                        <ValidatedDisclaimerExtension
                          className={classNames(classes.disclaimerContainerWrapper, 'disclaimer__container')}
                          i18n={legacyI18n}
                          campaignId={campaignId}
                          schema={DisclaimerAdExtension}
                          repository={disclaimerRepositoryRef.current}
                          validationProps={{
                            data: {
                              DisclaimerAdsEnabled,
                              AdExtensionAssociations: [], // AdExtensionAssociation in the UI
                              associatedDisclaimer, // Disclaimer Association from DB
                            },
                          }}
                          AllowDisclaimerEnabled
                          usePanel
                          isNativeAd // We only let native ad flow show this component
                        />
                      ) : (<SectionShimmer count={3} />)}
                    </ValidatedPanel>
                    <ValidatedPanel
                      key="call-to-action-opt-out"
                      title={<TextTitle
                        titleKey={_TL_('Call to action')}
                        icon={CallToActionImg}
                        topicKey="pop_BA_Audiences_EnableCTA"
                      />}
                      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                    >
                      {data && data.campaign ? (
                        <CallToActionOptOut
                          i18n={i18n}
                          data={data}
                          onDataChange={onDataChange}
                        />
                      ) : (<SectionShimmer count={3} />)}
                    </ValidatedPanel>
                  </Collapse>
                  {dealDetails && <DealImpressionPanel i18n={i18n} deal={dealDetails} serverContext={serverContext} />}
                </Stack>
                <Stack horizontal token={{ childrenGap: 14 }}>
                  {
                    advancedOptions.length > 0 && (
                      <ValidatedBootstrapPanel
                        defaultExpanded={false}
                        className={classNames(classes.collapsiblePanel, 'advanced-settings-panel')}
                        render={() => (
                          <>
                            <BootstrapPanel.Heading>
                              <BootstrapPanel.Title toggle>
                                {i18n.getString(_TL_('Advanced settings'))}
                              </BootstrapPanel.Title>
                            </BootstrapPanel.Heading>
                            <BootstrapPanel.Collapse>
                              <BootstrapPanel.Body>
                                <div className={classNames('advanced-campaign-section')}>
                                  <Collapse allPanelItems={allAdvancedCampaignSettingsSections} checkItems skipItemsTypeCheck activeKeys={advancedCampaignSettingsExpandedSections} setActiveKeys={setAdvancedCampaignSettingsExpandedSections}>
                                    <ValidatedPanel
                                      key="ad-schedule"
                                      title={<TextTitle
                                        titleKey={_TL_('Ad schedule')}
                                        key="ad-schedule"
                                        panelKey="ad-schedule"
                                        details={`${getAdScheduleDetails(i18n, adScheduleView && adScheduleView.current, true)}`}
                                        icon={adScheduleIcon}
                                      />}
                                    >
                                      {
                                        campaign || adGroup ? (
                                          <ValidatedLegacyAdSchedule
                                            isCreation
                                            hideMinuteOptions
                                            level={isEdit ? 'campaign' : 'adgroup'}
                                            entity={{ data: campaign }}
                                            validationProps={{
                                              data: adSchedules,
                                            }}
                                            hideUseCampaignSetting
                                            showBidAdjustmentColumn={isEdit}
                                            setAdScheduleView={setAdScheduleViewCallback}
                                          />
                                        ) : (<SectionShimmer />)
                                      }
                                    </ValidatedPanel>
                                    <ValidatedPanel
                                      key="advanced-url-options"
                                      title={<TextTitle titleKey={_TL_('Advanced URL options')} details={urlOptionsDetails} icon={UrlOptionsImg} />}
                                      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                                    >
                                      <span className="url-options-descriptions">
                                        {i18n.getString(_TL_('Set up the tracking and redirect information for your ads.'))}
                                      </span>
                                      {
                                        campaign &&
                                        <ValidatedUrlOptionsParameter
                                          i18n={i18n}
                                          validationProps={{
                                            data: {
                                              TrackingUrlTemplate: campaign.TrackingUrlTemplate,
                                              FinalUrlSuffix: campaign.FinalUrlSuffix,
                                              UrlCustomParameters: campaign.UrlCustomParameters,
                                            },
                                          }}
                                        />
                                      }
                                    </ValidatedPanel>
                                    <ValidatedPanel
                                      key="device-target"
                                      title={<TextTitle
                                        titleKey={_TL_('Device')}
                                        key="device-target"
                                        details={devicesDetails}
                                        icon={phoneDesktopIcon}
                                        iconStyles={{ width: 28 }}
                                        panelKey="device-target"
                                        topicKey="pop_BA_CampaignCreate_AudTargets_Device"
                                      />}
                                    >
                                      {
                                        (campaign && campaign.campaignLevelDeviceTargets) ? (
                                          <>
                                            <Device
                                              i18n={i18n}
                                              devices={campaign.campaignLevelDeviceTargets}
                                              isCTVSupported={false}
                                              validationPropsField="campaignLevelDeviceTargets"
                                              defaultSelectAll={shouldSelectAllDevices}
                                              key="campaignLevelDeviceTargets"
                                            />
                                            <ValidatedBidAdjustmentGrid
                                              data={data}
                                              validationProps={{
                                                data: data.campaign,
                                                stopPropagation: true,
                                              }}
                                              criterionType="Device"
                                              criterionLevel="CampaignCriterion"
                                              isAudienceEdit={isAudienceCampaignEdit}
                                            />
                                          </>
                                        ) : (<SectionShimmer />)
                                      }
                                    </ValidatedPanel>
                                    <ValidatedPanel
                                      key="web-exclusions"
                                      title={
                                        <TextTitle
                                          titleKey={WebsiteExclusionLists ? _TL_('Website control lists') : _TL_('Web Exclusions')}
                                          icon={webExclusionsIcon}
                                          iconStyles={{ width: 22 }}
                                          topicKey="pop_BA_WebsiteControlLists"
                                          details={
                                            WebsiteExclusionLists
                                            ? i18n.getString(_TL_('{{selected}} lists selected'), { selected: data?.websiteExclusionLists?.length })
                                            : ''}
                                        />}
                                      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                                      actuationProps={{
                                        expand: advancedSettingsExpand,
                                        expandKey: 'web-exclusions',
                                      }}
                                    >
                                      {WebsiteExclusionLists ? (
                                        <WebsiteLists selectedExclusions={data?.websiteExclusionLists} onExclusionsChange={onExclusionsListSelected} />
                                      ) : (
                                        <span className="web-exclusions-description">
                                          {i18n.getString(_TL_('Which websites should not show your ads and which IP addresses should be blocked from seeing your ads?'))}
                                        </span>
                                      )}
                                      <ServerValidatedWebExclusions
                                        showSiteExclusion
                                        showIPExclusion
                                        {...(WebsiteExclusionLists ? {
                                          showAccordion: true,
                                          siteInputProps: { placeholder: 'Enter one website per line.' },
                                          ipInputProps: { placeholder: 'Enter one IP address per line.' },
                                        } : {})}
                                        level={ScopeLevel.Campaign}
                                        exclusionData={data && data.exclusions ? data.exclusions : {}}
                                        setExclusionsCallback={setExclusionsCallback}
                                        errors={webExclusionsError}
                                        formErrorProps={{ filter: webExclusionPropertyVal }}
                                      />
                                    </ValidatedPanel>
                                    {
                                      campaignId && adGroupId && (
                                        <ValidatedPanel
                                          key="bid-adjustment"
                                          title={<TextTitle
                                            titleKey={_TL_('Bid adjustment')}
                                            icon={bidAdjustmentIcon}
                                          />}
                                          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                                        >
                                          <ValidatedBidAdjustment
                                            data={data}
                                            aid={aid}
                                            cid={cid}
                                            validationProps={{
                                              data: data.adGroup,
                                              onValidate: onAdGroupValidate,
                                              stopPropagation: true,
                                            }}
                                            adGroupId={adGroupId}
                                            campaignId={campaignId}
                                          />
                                        </ValidatedPanel>
                                      )
                                    }
                                    {
                                      campaign && campaignId && (
                                        <ValidatedPanel
                                          key="third-party-measurement-settings"
                                          title={<TextTitle
                                            headingClassName="third-party-measurement-header"
                                            headingRole="heading"
                                            headingLevel="2"
                                            titleKey={_TL_('Third-party measurement')}
                                            icon={Ruler}
                                            topicKey="pop_BA_Measurement_ThirdParty"
                                            details={formatVendorSettings({ verifiedTrackingSettingsValidationProps, i18n })}
                                          />}
                                          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                                          actuationProps={{
                                            expand: advancedSettingsExpand,
                                            expandKey: 'third-party-measurement',
                                          }}
                                        >
                                          <ThirdPartyMeasurementV3WithValidation
                                            i18n={i18n}
                                            aid={aid}
                                            cid={cid}
                                            validationProps={verifiedTrackingSettingsValidationProps}
                                            EnableDoubleVerify={EnableDoubleVerify}
                                            IsMSANISPOTEnabled={IsMSANISPOTEnabled}
                                          />
                                        </ValidatedPanel>
                                      )
                                    }
                                  </Collapse>
                                </div>
                              </BootstrapPanel.Body>
                            </BootstrapPanel.Collapse>
                          </>
                        )}
                      />
                    )
                  }
                </Stack>
              </div>
              <div className={classNames(classes.estimationPart, 'estimation-part')}>
                {isValidTargets && !isCTVOrDealFlow &&
                  <TargetingEstimation
                    data={data}
                    isCampaignCreationPlannerIntegrationEnabled={CampaignCreationPlannerIntegration}
                    PTPerfEstimationUIUpdate={PTPerfEstimationUIUpdate}
                    validationProps={{ field: 'reachEstimation' }}
                    audienceUsePrivacyCheck={AudienceUsePrivacyCheckV2}
                  />
                }
              </div>
            </ValidationGroup>
          </FormErrorGroup>
        </AttentionBeaconGroup>
      </ActuationGroup>
    </div>
  );
};

CampaignStepShell.propTypes = {
  data: PropTypes.shape({
    adGroup: PropTypes.shape({
      Id: PropTypes.number,
      UseOptimizedTargeting: PropTypes.bool,
      BiddingScheme: PropTypes.shape({
        Type: PropTypes.string,
      }),
      campaignLevelDeviceTargets: PropTypes.arrayOf(PropTypes.shape({
        Id: PropTypes.number,
        Type: PropTypes.string,
      })),
    }),
    campaign: PropTypes.shape({
      Id: PropTypes.number,
      Budget: PropTypes.shape({
        Type: PropTypes.string,
        Amount: PropTypes.number,
      }),
      StartDate: PropTypes.string,
      EndDate: PropTypes.string,
      UseCampaignLevelDates: PropTypes.bool,
      TrackingUrlTemplate: PropTypes.string,
      FinalUrlSuffix: PropTypes.string,
      UrlCustomParameters: PropTypes.string,
      campaignLevelDeviceTargets: PropTypes.arrayOf(PropTypes.shape({
        Id: PropTypes.number,
        Type: PropTypes.string,
      })),
    }),
    exclusions: PropTypes.shape({
      campaignExclusions: PropTypes.shape({
        [webExclusionPropertyVal]: PropTypes.arrayOf(PropTypes.shape({
          Id: PropTypes.number,
          Type: PropTypes.string,
        })),
      }),
    }),
    websiteExclusionLists: PropTypes.arrayOf(PropTypes.shape({
      Id: PropTypes.number,
    })),
  }),
  onDataChange: PropTypes.func.isRequired,
  error: PropTypes.arrayOf(PropTypes.shape({
    property: PropTypes.string,
    message: PropTypes.string,
  })),
};

CampaignStepShell.defaultProps = {
  data: null,
  error: [],
};
